import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { ListingCardLocation } from 'src/common/types/common/ListingCard';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import ListingCard from '../ListingCard';
import type {
  Breadcrumb, FullBoatSearchDetail, OptimizedBoatSearchDetail,
} from '../../types/api/v4/boats/search';
import getBreadcrumbs from '../../utils/getBreadcrumbs';
import getClassNames from '../../utils/getClassNames';
import { ListingCardBoat } from '../../types';

import styles from './ListingCardList.module.scss';
import { InquirySentIndicator } from '../../types/api/v4/trips';
import GulperImpressions from '../GulperImpressions';

type ActiveBoat = Pick<FullBoatSearchDetail, 'id'>;

/**
 * The number of images to load eagerly
 */
const IMAGES_TO_LOAD = 1;

function isPublishedBoat(
  listing: ListingCardBoat,
): listing is OptimizedBoatSearchDetail {
  return Boolean(
    listing.active
    && listing.user_details !== null,
  );
}

export type ListingCardListProps = {
  parentBreadcrumbs?: Breadcrumb[];
  listings: ListingCardBoat[];
  activeBoat?: ActiveBoat | null;
  activeBoatRef?: React.Ref<HTMLDivElement>;
  /**
   * `primary` will eagerly load the first N images.
   * `secondary` will load all images lazily.
   */
  variant?: 'primary' | 'secondary';
  classNameModifier?: string;
  inquiryIndicators?: InquirySentIndicator[];
  /**
   * If set to true, the fetchpriority property is applied to the first item
   * in the list's main image
   */
  enableFetchPriority?: boolean;
  cardLocation?: ListingCardLocation;
};

const listingCardVariant = {
  popup: 'popup',
  favorites: 'favorites',
};

const ListingCardList: FC<ListingCardListProps> = ({
  parentBreadcrumbs = [],
  listings,
  activeBoat,
  activeBoatRef,
  variant = 'secondary',
  classNameModifier = '',
  inquiryIndicators = [],
  enableFetchPriority = true,
  cardLocation,
  ...rest
}) => {
  const [t] = useTranslation('listing-page');
  const modifier = listingCardVariant[classNameModifier] || undefined;
  const { isMobileNativeWebView } = useMobileNativeWebView();
  return (
    <div {...rest} className={getClassNames(styles, 'root', classNameModifier)}>
      {listings.map((listing, index) => {
        const isActive = activeBoat?.id === listing.id;
        const hasActiveInquiry = inquiryIndicators.some(
          ({ listing_id }) => listing_id === listing.id,
        );
        if (isPublishedBoat(listing)) {
          return (
            <ListingCard
              listingId={listing.id}
              key={listing.id}
              cardRef={isActive ? activeBoatRef : null}
              isActive={isActive}
              listingUrl={listing.listing_url}
              imageSrc={listing.photo_url}
              capacity={listing.capacity ? t('guestCapacity', { count: listing.capacity }) : ''}
              headline={listing.headline}
              rating={listing.review_average && parseFloat(listing.review_average)}
              isSuperOwner={listing.user_details.is_super_owner}
              breadcrumbs={getBreadcrumbs(listing.breadcrumbs, parentBreadcrumbs)}
              category={listing.cat_loc_breadcrumb}
              awards={listing.user_details?.awards}
              loading={variant === 'primary' && index < IMAGES_TO_LOAD ? 'eager' : 'lazy'}
              fetchpriority={index === 0 && enableFetchPriority ? 'high' : null}
              instantBookingAvailable={listing.has_available_instabook && listing.instabook_enabled}
              variant={modifier}
              photos={listing.photos}
              inquirySent={hasActiveInquiry}
              bookingMotivator={listing.booking_motivators_label}
              isHighlighted={listing.is_highlighted}
              searchPricing={listing.search_price_nested}
              cardLocation={cardLocation}
              snoozeStartDate={listing.snooze_start_date}
              snoozeEndDate={listing.snooze_end_date}
              withFavorite={!isMobileNativeWebView}
            />
          );
        }
        return (
          <ListingCard
            listingId={listing.id}
            key={listing.id}
            isActive={isActive}
            imageSrc={listing.photo_url}
            headline={listing.headline}
            withFavorite={!isMobileNativeWebView}
          />
        );
      })}
      <GulperImpressions data={listings} />
    </div>
  );
};

export default ListingCardList;
